import {Component, DestroyRef, Input} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MAT_DATE_FORMATS} from '@angular/material/core';
import {ReferenceBookType, SubscriberCategory} from '@npck/af-frontend-api';
import {IIN_PATTERN} from '@npck/libs/commons';
import {DateForm} from '../../../../../components/date-picker-field/date-picker-field.component';
import {PhoneInfo} from '../../../../../domain/phone-utils';
import {DATE_TIME_FORMATS} from '../../../../../infra/date-time.adapter';

const KAZAKHSTAN_COUNTRY_CODE = '398';
const MAX_SUBSCRIBER_MSISDNS = 50;

@Component({
    selector: 'af-msisdn-fraud-form',
    templateUrl: './msisdn-fraud-form.component.html',
    providers: [
        {provide: MAT_DATE_FORMATS, useValue: DATE_TIME_FORMATS},
    ],
    standalone: false,
})
export class MsisdnFraudFormComponent {
    @Input() form: FormGroup<MsisdnFraudEntryForm>;
    @Input() lockedControls: AbstractControl[] = [];

    ReferenceBookType = ReferenceBookType;
    DATE_TIME_FORMATS = DATE_TIME_FORMATS;

    get canAddMoreSubscriberMsisdns(): boolean {
        return (this.form?.controls?.subscriberMsisdns?.length ?? 0) < MAX_SUBSCRIBER_MSISDNS;
    }

    addSubscriberMsisdn(): void {
        this.form.controls.subscriberMsisdns.push(new FormGroup<SubscriberMsisdnForm>({
            msisdn: new FormControl(null),
            selectedPhoneInfo: new FormControl(null),
        }));
    }

    removeSubscriberMsisdn(index: number): void {
        this.form.controls.subscriberMsisdns.removeAt(index);
    }
}

export function buildMsisdnFraudEntryControls(
    form: FormGroup<MsisdnFraudEntryForm>,
    formBuilder: FormBuilder,
    destroyRef: DestroyRef,
): void {
    form.addControl('contactDate', formBuilder.group<DateForm>({
        date: new FormControl(null),
        time: new FormControl(null),
    }));
    form.addControl('category', new FormControl(SubscriberCategory.SUSPECTED, [Validators.required]));
    form.addControl('isResident', new FormControl(true));
    form.addControl('iin', new FormControl(null, [Validators.required, Validators.pattern(IIN_PATTERN)]));
    form.addControl('passportNumber', new FormControl(null));
    form.addControl('lastName', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
    form.addControl('firstName', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
    form.addControl('middleName', new FormControl(null, [Validators.maxLength(255)]));
    form.addControl('country', new FormControl(KAZAKHSTAN_COUNTRY_CODE));
    form.addControl('subscriberMsisdns', formBuilder.array([] as FormGroup<SubscriberMsisdnForm>[]));

    form.controls.isResident.valueChanges
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe(isResident => {
            if (isResident) {
                form.controls.iin.setValidators([Validators.required, Validators.pattern(IIN_PATTERN)]);
                form.controls.passportNumber.clearValidators();
                form.controls.passportNumber.reset();
                form.controls.country.clearValidators();
                if (form.controls.country.enabled) {
                    form.controls.country.setValue(KAZAKHSTAN_COUNTRY_CODE);
                }
            } else {
                form.controls.iin.clearValidators();
                form.controls.iin.reset();
                form.controls.passportNumber.setValidators([Validators.required, Validators.maxLength(255)]);
                form.controls.country.setValidators([Validators.required]);
                if (form.controls.country.enabled) {
                    form.controls.country.reset();
                }
            }
            form.controls.iin.updateValueAndValidity({emitEvent: false});
            form.controls.passportNumber.updateValueAndValidity({emitEvent: false});
            form.controls.country.updateValueAndValidity({emitEvent: false});
        });
}

export interface MsisdnFraudEntryForm {
    msisdn?: FormControl<string>;
    selectedPhoneInfo?: FormControl<PhoneInfo>;
    operator?: FormControl<string>;
    activationDate?: FormGroup<DateForm>;
    terminationDate?: FormGroup<DateForm>;
    status?: FormControl<string>;
    reason?: FormControl<string>;
    contactDate?: FormGroup<DateForm>;
    category?: FormControl<string>;
    isResident?: FormControl<boolean>;
    iin?: FormControl<string>;
    passportNumber?: FormControl<string>;
    lastName?: FormControl<string>;
    firstName?: FormControl<string>;
    middleName?: FormControl<string>;
    country?: FormControl<string>;
    subscriberMsisdns?: FormArray<FormGroup<SubscriberMsisdnForm>>;
}

export interface SubscriberMsisdnForm {
    msisdn?: FormControl<string>;
    selectedPhoneInfo?: FormControl<PhoneInfo>;
}
